import { FC, useMemo, useState } from 'react';
import { userSelector } from 'slices/auth.slice';
import { useAppSelector } from 'slices/store';
import { TPartialAttributesMatchingRow } from '@timeedit/types/lib/types';
import { Alert, Button, Tooltip, Typography } from '@timeedit/ui-components';
import { List, Modal, Progress, Space } from '@timeedit/ui-components/antd';
import { mapRowToAffiliation } from './mapRowToAffiliation';
import {
  BulkCreateItem,
  BulkCreateResult,
  useBulkCreateAffiliations,
} from './useBulkCreateAffiliations';

const { Text } = Typography;

type Props = {
  authConfigId?: string;
  legacyRows: TPartialAttributesMatchingRow[];
  legacyRowsDirty: boolean;
  existingNewAffiliationsCount: number;
  onMigrationComplete?: (results: BulkCreateResult[]) => void;
};

const MigrateLegacyAffiliationsButton: FC<Props> = ({
  authConfigId,
  legacyRows,
  legacyRowsDirty,
  existingNewAffiliationsCount,
  onMigrationComplete,
}) => {
  const authUser = useAppSelector(userSelector);
  const isRootUser = useMemo(
    () => authUser?.scopes?.includes('TE_ROOT_USER'),
    [authUser]
  );

  const {
    run: runBulkCreate,
    validateItems,
    isRunning: isMigrating,
    progress,
  } = useBulkCreateAffiliations();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [results, setResults] = useState<BulkCreateResult[]>([]);
  const [hasRun, setHasRun] = useState(false);

  const items = useMemo<BulkCreateItem[]>(
    () =>
      legacyRows.map((row, rowIdx) => {
        const affiliation = mapRowToAffiliation(row, rowIdx);
        return {
          rowIdx,
          label: affiliation.label,
          affiliation,
        };
      }),
    [legacyRows]
  );

  const failed = results.filter((r) => r.error);
  const succeeded = results.filter((r) => !r.error);
  const hasFailedMigration = failed.length > 0;

  const showButton = isRootUser && !!authConfigId;

  const disabledMessage =
    items.length === 0
      ? 'There are no legacy affiliations to migrate.'
      : legacyRowsDirty
        ? 'There are unsaved changes to the legacy affiliations. Please save or discard changes before migration.'
        : existingNewAffiliationsCount > 0
          ? `There are already ${existingNewAffiliationsCount} affiliation${
              existingNewAffiliationsCount === 1 ? '' : 's'
            } in the new format. Please delete any existing affiliations before migration.`
          : '';

  // Render nothing only when the button is hidden AND the modal isn't open.
  // Otherwise an in-progress / just-completed migration would unmount its own
  // modal the moment the count query refetches and reports the new
  // affiliations.
  if (!showButton && !isModalOpen) return null;

  const runMigration = async (toMigrate: BulkCreateItem[]) => {
    if (!authConfigId) return;

    const newResults = await runBulkCreate(authConfigId, toMigrate);

    setResults((prev) => {
      // Merge with previous results: replace entries for retried rows.
      // Compute the merged array synchronously so it can be passed to the
      // callback before the next render cycle.
      const retriedIdx = new Set(toMigrate.map((i) => i.rowIdx));
      const mergedResults = [
        ...prev.filter((r) => !retriedIdx.has(r.rowIdx)),
        ...newResults,
      ];
      onMigrationComplete?.(mergedResults);
      return mergedResults;
    });
    setHasRun(true);
  };

  const handleOpen = () => {
    setIsModalOpen(true);
    if (hasFailedMigration) {
      setResults(validateItems(items));
    } else {
      setResults([]);
      setHasRun(false);
    }
  };

  const handleClose = () => {
    if (isMigrating) return;
    setIsModalOpen(false);
  };

  const handleStart = () => {
    runMigration(items);
  };

  const footer =
    failed.length > 0 || (hasRun && succeeded.length > 0)
      ? [
          <Button
            key="close"
            type="primary"
            onClick={handleClose}
            disabled={isMigrating}
          >
            Close
          </Button>,
        ]
      : [
          <Button key="cancel" onClick={handleClose} disabled={isMigrating}>
            Cancel
          </Button>,
          <Button
            key="ok"
            type="primary"
            onClick={handleStart}
            loading={isMigrating}
          >
            Migrate {items.length}
          </Button>,
        ];

  return (
    <>
      {showButton && (
        <Tooltip title={disabledMessage}>
          <Button
            size="small"
            onClick={handleOpen}
            disabled={!!disabledMessage}
          >
            {`Migrate ${items.length} legacy affiliation${
              items.length === 1 ? '' : 's'
            }`}
          </Button>
        </Tooltip>
      )}
      <Modal
        title="Migrate legacy affiliations"
        open={isModalOpen}
        onCancel={handleClose}
        maskClosable={!isMigrating}
        closable={!isMigrating}
        footer={footer}
      >
        {existingNewAffiliationsCount > 0 && !hasRun && (
          <Alert
            type="error"
            showIcon
            message={`There are already ${existingNewAffiliationsCount} affiliation${
              existingNewAffiliationsCount === 1 ? '' : 's'
            } in the new format. Please delete any existing affiliations before migration.`}
          />
        )}
        {!isMigrating &&
          existingNewAffiliationsCount === 0 &&
          failed.length === 0 && (
            <Text>
              This will create {items.length} new affiliation
              {items.length === 1 ? '' : 's'} from the legacy configuration. The
              original data will not be modified.
            </Text>
          )}
        {isMigrating && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>Migrating affiliations...</Text>
            <Progress percent={progress} />
          </Space>
        )}
        {hasRun &&
          !isMigrating &&
          failed.length === 0 &&
          succeeded.length > 0 && (
            <Alert
              type="success"
              showIcon
              message={`Successfully migrated all ${
                succeeded.length
              } affiliation${succeeded.length === 1 ? '' : 's'}.`}
            />
          )}
        {hasRun && !isMigrating && failed.length > 0 && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Alert
              type={'warning'}
              showIcon
              message={`${succeeded.length}/${items.length} migrated${
                failed.length > 0
                  ? `, ${failed.length} failed. Please fix the issues below and try migration again.`
                  : ''
              }`}
            />
            {failed.length > 0 && (
              <List
                size="small"
                header={<Text strong>Failed migrations</Text>}
                bordered
                dataSource={failed}
                renderItem={(r) => (
                  <List.Item>
                    <Space direction="vertical" size={0}>
                      <Text strong>{r.label}</Text>
                      <Text type="danger">{r.error}</Text>
                    </Space>
                  </List.Item>
                )}
              />
            )}
          </Space>
        )}
      </Modal>
    </>
  );
};

export default MigrateLegacyAffiliationsButton;
